import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth/types';
import { useListPayStations } from 'src/features/payment/api/useListPayStations';
import CellPayStationActions from 'src/features/payment/components/cell/CellPayStationActions';
import CreatePayStationFormModal from 'src/features/payment/components/forms/CreatePayStationFormModal';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { PayStation } from 'src/features/payment/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import {
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { generalMessages } from 'src/registry/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import { PATH } from 'src/features/navigation/utils/constants';
import { ICON_MAP } from 'src/utils/constants';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useListSites } from 'src/features/sites/api/useListSites';
import React from 'react';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';

const columnHelper = createColumnHelper<PayStation>();

const PayStationsTable = () => {
    const { formatMessage, locale } = useIntl();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryFiltersOptions, ...filters } = useFilters();

    const managePayStationsPermission = useHasPermissions([PERMISSION.MANAGE_PAY_STATIONS]);
    const { parkingId } = useParking();

    const {
        data: payStationsData,
        isError,
        isLoading,
    } = useListPayStations({
        parkingId,
        ...queryPaginationOptions,
        ...querySearchOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
    });

    const { data: sites } = useListSites({ parkingId });

    const filteredEmptySiteTranslations = React.useMemo(() => {
        return sites?.results?.filter((site) => site.translations.length > 0);
    }, [sites?.results]);

    const sitesFilterOptions = React.useMemo(
        () =>
            filteredEmptySiteTranslations?.map((site) => {
                const translatedSite = site.translations.find((s) => s.language === locale);

                return { value: site.id, label: translatedSite?.name ?? site.translations[0]?.name ?? site.id };
            }),
        [filteredEmptySiteTranslations, locale],
    );

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => (
                <Link
                    to={`/${PATH.PAYMENTS}/pay-station/${row.original.id}`}
                    className="text-primary-700 hover:text-primary-600 cursor-pointer font-medium"
                >
                    {row.original.name}
                    <FontAwesomeIcon icon={ICON_MAP.ARROW_UP_RIGHT} />
                </Link>
            ),
            meta: {
                title: formatMessage(generalMessages.name),
            },
        }),
        columnHelper.accessor('code', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(paymentsMessages.code),
            },
        }),
        columnHelper.accessor('site', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: ({ row }) => {
                return row.original.site.translations?.find((item) => item.language === locale)?.name ?? '-';
            },
            meta: {
                title: formatMessage(paymentsMessages.site),
                options: sitesFilterOptions,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellPayStationActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<PayStation, any>
            columns={columns}
            data={payStationsData}
            pagination={pagination}
            sorting={sorting}
            filters={filters}
            search={search}
            semantics={{
                title: formatMessage(paymentsMessages.payStations),
                entryName: formatMessage(paymentsMessages.payStation).toLowerCase(),
                entryNames: formatMessage(paymentsMessages.payStations).toLowerCase(),
            }}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                newEntry: managePayStationsPermission ? CreatePayStationFormModal : undefined,
            }}
        />
    );
};

export default PayStationsTable;
